import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IPopupSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IPopupGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { IPopup, useStores } from 'src/models';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { popupApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { PopupFormDialog } from './PopupFormDialog';

interface IPopupGridProps {
  searchData: IPopupSFilter;
}

export const PopupGrid = ({ searchData }: IPopupGridProps) => {
  const { businessStore } = useStores();
  const { getPopupGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { t } = useTranslation();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IPopupGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 목록 조회
  const getPopupData = useCallback(async () => {
    const pageParams = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    await popupApi.getPopup(pageParams).then((res) => {
      if (res && res.data) {
        const newGridRows = res.data.map((popup, i) => {
          return { ...popup, id: popup.popupSid ?? i };
        });
        setGridRows(newGridRows as IPopupGridRow[]);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [searchData]);

  useEffect(() => {
    getPopupData();
  }, [getPopupData]);

  // 등록 버튼: 등록 dialog open
  const handleAddBusiness = () => {
    openDialog({
      id: FORM_IDS.POPUP,
      formType: FormType.CREATE,
      maxWidth: 'xl',
      dialogContent: (
        <PopupFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IPopupGridRow>) => {
    callApiWithLoading(async () => {
      await popupApi.getDetail(param.row.popupSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.POPUP,
            formType: FormType.UPDATE,
            maxWidth: 'xl',
            dialogContent: (
              <PopupFormDialog
                formType={FormType.UPDATE}
                onSubmitSuccess={handleSubmitSuccess}
                selectedData={res.data as IPopup}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.POPUP);
    getPopupData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    setPagination({
      ...pagination,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    });
    getPopupData();
  };

  return (
    <ContentBox>
      <ContentBoxButtons>
        <ContentButtonCreate onClick={handleAddBusiness}>
          {t('common.button.register')}
        </ContentButtonCreate>
      </ContentBoxButtons>
      <CDataGridPro
        columns={getPopupGridCol()}
        rows={gridRows}
        rowCount={toJS(businessStore.pagination.totalElements)}
        paginationModel={{
          page: businessStore.pagination.page - 1,
          pageSize: businessStore.pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        onRowDoubleClick={handleRowDoubleClick}
        slots={{ footer: CGridFooter }}
      />
    </ContentBox>
  );
};

export default observer(PopupGrid);
